/**
 * #881 Scope 2 — which of an ability's OWN applied auras a second press of that
 * ability's key should CANCEL (#922's `cancelAura`) instead of re-casting.
 *
 * THE PROBLEM. Ice Block now freezes the mage (`controlFlag: move/cast/attack`),
 * so while it is up the ability's key is completely inert: `canAct` is false, so
 * a `cast` input is dropped by the sim. #922 already shipped the general way out
 * — right-click the buff chip on `SelfAuraBar` — and the owner asked for the
 * WoW-style convenience on top: pressing the ability's own key again releases
 * the buff. That is a pure CLIENT rebind onto an existing input; no sim,
 * protocol or wire change (see #922's `step.ts` `cancelOwnBuff`).
 *
 * WHY THIS IS NOT "any self-buff, second press cancels it". Seventeen shipped
 * abilities apply a top-level self aura (Battle Shout, Ice Barrier, Stealth,
 * Move Quick, Spell Reflect, …). A blanket rule would hijack every one of their
 * re-presses — pressing Battle Shout while Battle Shout is up would DROP the
 * buff instead of refreshing it, which is a gameplay regression in 16 places to
 * fix a keybind in one. The rebind is therefore gated on the ONE property that
 * makes Ice Block's key inert in the first place:
 *
 *   the applied aura CARRIES A `controlFlag` THAT BLOCKS `cast`.
 *
 * A buff that locks its holder out of casting cannot be re-cast by definition,
 * so routing that press to `cancelAura` can never steal a legal cast — and every
 * future self-lockout defensive (a Zhonya's/bubble-shaped ability) inherits the
 * behaviour with no table to maintain. `ice-block-buff` is the ONLY shipped buff
 * with a `controlFlag` at all; every other `controlFlag` aura is a debuff (stun,
 * mez, the roots), which `cancelAura`'s polarity gate refuses anyway.
 *
 * WHAT REACHES THE CLIENT BUNDLE. Only a DERIVED BOOLEAN per aura def — never
 * the primitives themselves. Same narrowing discipline as `abilityFacts.ts`,
 * whose schema structurally strips `effects` yet lets one derived sub-field
 * through (#502's `appliedAuraDefIds`, which this module composes with): read
 * the raw JSON at build time via the literal `import.meta.glob` idiom, then keep
 * exactly one fact. And it is not privileged information — "the ice-blocked mage
 * cannot act" is visible to both players the instant the ability is cast.
 */

import { z } from "zod";
import { AURA_DEF_ID_SCHEMA, toAuraDefId, type AuraDefId, type ChampionId } from "@pit/protocol";
import { abilityFacts } from "./abilityFacts.js";
import { abilityTargetType } from "./abilityTargetType.js";

/**
 * Narrow aura schema: `id` plus the one primitive fact this module derives.
 * `z.object` (not `strictObject`) strips `polarity`/`tags`/`stacking`/
 * `durationTicks`/etc, and each primitive is narrowed to `{kind, flag}` — a
 * `statMod`'s `value`, a `periodic`'s `effect`, an `absorbPool`'s `amount` and
 * every other balance-sensitive field are dropped by the parse, not by
 * convention.
 */
const AURA_LOCKOUT_SCHEMA = z.object({
  id: AURA_DEF_ID_SCHEMA,
  primitives: z.array(z.object({ kind: z.string(), flag: z.string().optional() })),
});

/**
 * Build-time set of every aura def id whose primitives include a `controlFlag`
 * blocking `cast` — i.e. "holding this aura prevents its holder from casting".
 * Same literal-`import.meta.glob` rule as `auraLabels.ts`/`abilityFacts.ts`:
 * the call MUST be the literal form for Vite's static transform.
 */
function loadCastLockingAuras(): ReadonlySet<string> {
  const modules = import.meta.glob<string>(
    "../../../../packages/content/data/auras/*.json",
    { eager: true, query: "?raw", import: "default" },
  );
  const out = new Set<string>();
  for (const path of Object.keys(modules).sort()) {
    const raw = modules[path];
    if (raw === undefined) continue;
    const parsed = AURA_LOCKOUT_SCHEMA.parse(JSON.parse(raw) as unknown);
    if (parsed.primitives.some((p) => p.kind === "controlFlag" && p.flag === "cast")) {
      out.add(parsed.id);
    }
  }
  return out;
}

/** Every aura def whose holder cannot cast (stun/mez/Ice Block today). */
export const CAST_LOCKING_AURA_DEF_IDS: ReadonlySet<string> = loadCastLockingAuras();

/** Does holding `defId` prevent its holder from casting? Pure lookup. */
export function auraLocksCasting(defId: string): boolean {
  return CAST_LOCKING_AURA_DEF_IDS.has(defId);
}

/**
 * The aura def id(s) a second press of `slot` on `championId` may CANCEL: the
 * ability's own top-level `applyAura` defs (#502's `appliedAuraDefIds`),
 * filtered to the cast-locking ones — and only for a `targetType: "self"`
 * ability. Empty for every shipped ability except Ice Block.
 *
 * The `targetType` gate is what keeps ENEMY CC out: Kidney Shot (`stun`),
 * Polymorph and Poison Dart all apply cast-locking auras too, but to somebody
 * else. `appliedAuraDefIds` carries no target information, so without this gate
 * a Rogue holding a stun (having been stunned back) would see their Kidney Shot
 * key re-routed. Two further guards make that unreachable anyway — the mapper
 * requires the caster to actually HOLD the def, and both mapper and sim require
 * `polarity === "buff"` — but the narrowest correct table is the one that does
 * not rely on them.
 *
 * Whether the caster actually holds one right now is deliberately NOT decided
 * here: that is live wire state, read by `mapper.ts` off the snapshot.
 */
export function selfCancelAuraDefs(championId: ChampionId, slot: number): readonly AuraDefId[] {
  if (abilityTargetType(championId, slot) !== "self") return [];
  const applied = abilityFacts(championId, slot)?.appliedAuraDefIds;
  if (applied === undefined) return [];
  const out: AuraDefId[] = [];
  for (const def of applied) {
    if (auraLocksCasting(def)) out.push(toAuraDefId(def));
  }
  return out;
}
